import React, { useState } from "react";
import Styled from "./AllResources.styled";
import LatestBlogs from "../LatestBlogs";
import LatestCases from "../LatestCases";
import LatestAssets from "../LatestAssets";
import LatestEvents from "../LatestEvents";
import LatestNews from "../LatestNews";
import DeveloperDocs from "../DeveloperDocs";
import { IWPLink } from "@/src/types";
import { Flex } from "@/styles/global";
import { SmallHeading1 } from "@/styles/typography";

export interface IAllResourcesProps {
  blockId: string;
  news: IWPLink;
  events: IWPLink;
  cases: IWPLink;
  assets: IWPLink;
  blogs: IWPLink;
  developerDocs: {
    title: string;
    links: {
      category: string;
      title: string;
      link: IWPLink;
    }[];
  };
}

type Section =
  | "News"
  | "Events"
  | "Partner Cases"
  | "Assets"
  | "Blogs"
  | "Developer Docs";

const SectionButton: React.FC<{
  section: Section;
  active: boolean;
  onClick: (section: Section) => void;
}> = ({ section, active, onClick }) => (
  <Styled.Filter
    $active={active}
    onClick={(e) => {
      e.preventDefault();
      onClick(section);
    }}
  >
    <SmallHeading1>{section}</SmallHeading1>
  </Styled.Filter>
);

const AllResources: React.FC<IAllResourcesProps> = ({
  blockId = "",
  news,
  events,
  cases,
  assets,
  blogs,
  developerDocs,
}) => {
  const [activeSection, setActiveSection] = useState<Section>("News");

  const sections: Section[] = [
    "News",
    "Events",
    "Partner Cases",
    "Assets",
    "Blogs",
    "Developer Docs",
  ];

  const handleScrollToSection = (section: Section) => {
    setActiveSection(section);
    const targetSection = document.getElementById(section);
    if (targetSection) {
      targetSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <Styled.Section id={blockId}>
      <Styled.ScrollContainer>
        <Styled.Filters>
          <Flex $gap={16} $desktopGap={32}>
            {sections.map((section) => (
              <SectionButton
                key={section}
                section={section}
                active={activeSection === section}
                onClick={handleScrollToSection}
              />
            ))}
          </Flex>
        </Styled.Filters>
      </Styled.ScrollContainer>

      <LatestNews title="News" link={news} />
      <LatestEvents title="Events" link={events} />
      <LatestCases title="Partner Cases" link={cases} />
      <LatestAssets title="Assets" link={assets} />
      <LatestBlogs title="Blog" link={blogs} />
      {developerDocs.links && <DeveloperDocs blockId="" {...developerDocs} />}
    </Styled.Section>
  );
};

export default AllResources;
